import { useMutation, useQuery } from '@apollo/client/react';
import { datadogRum } from '@datadog/browser-rum';
import { IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import Back from 'assets/icons/back.svg?react';
import WistiaPlayer from 'components/common/WistiaPlayer';
import {
  GetOnDemandDetailsDocument,
  UpdateProgressDocument,
} from 'graphql/types';
import React from 'react';
import { useNavigate } from 'react-router';

interface FullScreenPlayerProps {
  contentId: string;
  hashedId: string;
  sectionId?: string;
}

const BackButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.baseUnit * 2,
  left: theme.baseUnit * 2,
  zIndex: 1,
  '& svg': {
    '& path': {
      stroke: 'white',
    },
  },
}));

const FullScreenPlayer: React.FC<FullScreenPlayerProps> = ({
  contentId,
  hashedId,
  sectionId,
}) => {
  const [updateProgress] = useMutation(UpdateProgressDocument);
  const navigate = useNavigate();

  const goBack = () => {
    if (window.history?.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate('/', { replace: true });
    }
  };

  // Validating this is an on demand video, to determine whether to track progress
  const { data, loading } = useQuery(GetOnDemandDetailsDocument, {
    variables: {
      id: contentId,
    },
  });

  const onProgressEvent = (progress: number) => {
    if (data && data.content !== null) {
      try {
        datadogRum.addAction('onDemandVideoProgress', {
          sectionId,
          contentId,
          progress,
        });
        updateProgress({
          variables: {
            sectionId,
            contentId,
            progress,
          },
        });
      } catch (error) {
        datadogRum.addError(error);
      }
    }
  };

  return (
    <FullScreenContainer id="fullScreenPlayerContainer">
      <BackButton aria-label="back" onClick={goBack}>
        <Back />
      </BackButton>
      {!loading && (
        <WistiaPlayer
          hashedId={hashedId}
          videoId={contentId}
          videoFoam={false}
          onPause={onProgressEvent}
          onProgress={onProgressEvent}
        />
      )}
    </FullScreenContainer>
  );
};

const FullScreenContainer = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  height: '100vh',
  background: theme.colors.gray['900'],
  zIndex: 2000,
  display: 'flex',
  alignItems: 'center',
}));

export default FullScreenPlayer;
